import { ReactNode, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { 
  MessageSquare, 
  Globe, 
  FileText, 
  Code, 
  BarChart3, 
  Users, 
  CreditCard,
  LogOut,
  Loader2,
  Menu,
  Settings,
  Rocket,
  TrendingUp,
  ShoppingBag,
  UserCircle,
  ChevronDown,
  ChevronUp,
  Moon,
  Sun,
  Brain,
  Wallet,
  Plus,
  PlusCircle,
  Ticket,
  Zap,
  Mail,
  Plug,
  Activity,
  Headphones,
  LayoutTemplate,
  LayoutGrid,
  LayoutDashboard,
  Shield,
  Database,
  HelpCircle
} from 'lucide-react';

import { cn, normalizeApiUrl } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from 'next-themes';
import { OnboardingChoiceModal } from './OnboardingChoiceModal';
import { WizardBanner } from './WizardBanner';
import { useToast } from '@/hooks/use-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Helper to check if user is a system admin (hardcoded emails or admin role)
const isSystemAdmin = (user: any, isAdminRole?: boolean) => {
  const adminEmails = ['yanwar.pro@gmail.com'];
  const userEmail = (user?.email || user?.user_metadata?.email || '').toLowerCase().trim();
  const isFound = adminEmails.some(email => email.toLowerCase() === userEmail);
  return isAdminRole === true || isFound;
};

function readCachedBranding() {
  if (typeof window === 'undefined') {
    return { brandName: null, logoUrl: null, host: null as string | null };
  }
  try {
    const raw = window.localStorage.getItem('agency_branding');
    if (!raw) return { brandName: null, logoUrl: null, host: null as string | null };
    const parsed = JSON.parse(raw) as { brand_name?: unknown; logo_url?: unknown; host?: unknown };
    const host =
      typeof parsed.host === 'string' && parsed.host.length > 0 ? parsed.host.toLowerCase() : null;
    return {
      brandName: typeof parsed.brand_name === 'string' ? parsed.brand_name : null,
      logoUrl: typeof parsed.logo_url === 'string' ? parsed.logo_url : null,
      host,
    };
  } catch {
    return { brandName: null, logoUrl: null, host: null as string | null };
  }
}

function readCachedPlan(userId?: string | null) {
  if (typeof window === 'undefined' || !userId) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem('dashboard_plan_cache');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      userId?: unknown;
      userPlanName?: unknown;
      personalPlanName?: unknown;
      isTeamMember?: unknown;
      cachedAt?: unknown;
    };
    if (parsed.userId !== userId) return null;
    // Cache TTL: 1 minute (reduced from 5 min to ensure plan updates are reflected quickly)
    const CACHE_TTL_MS = 1 * 60 * 1000;
    const cachedAt = typeof parsed.cachedAt === 'number' ? parsed.cachedAt : 0;
    if (Date.now() - cachedAt > CACHE_TTL_MS) {
      window.localStorage.removeItem('dashboard_plan_cache');
      return null;
    }
    const userPlanName =
      typeof parsed.userPlanName === 'string' && parsed.userPlanName.length > 0
        ? parsed.userPlanName
        : 'Free';
    const personalPlanName =
      typeof parsed.personalPlanName === 'string' && parsed.personalPlanName.length > 0
        ? parsed.personalPlanName
        : 'Free';
    const isTeamMember = typeof parsed.isTeamMember === 'boolean' ? parsed.isTeamMember : false;
    return { userPlanName, personalPlanName, isTeamMember };
  } catch {
    return null;
  }
}

function writeCachedPlan(
  userId: string,
  data: { userPlanName: string; personalPlanName: string; isTeamMember: boolean }
) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      'dashboard_plan_cache',
      JSON.stringify({
        userId,
        userPlanName: data.userPlanName,
        personalPlanName: data.personalPlanName,
        isTeamMember: data.isTeamMember,
        cachedAt: Date.now(),
      })
    );
  } catch (error) {
    console.error('Failed to cache dashboard_plan_cache', error);
  }
}

function readCachedWebsites(userId?: string | null): AccessibleWebsite[] {
  if (typeof window === 'undefined' || !userId) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(`dashboard_websites_cache_${userId}`);
    if (!raw) return [];
    return JSON.parse(raw) as AccessibleWebsite[];
  } catch {
    return [];
  }
}

function writeCachedWebsites(userId: string, websites: AccessibleWebsite[]) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(`dashboard_websites_cache_${userId}`, JSON.stringify(websites));
  } catch (error) {
    console.error('Failed to cache websites', error);
  }
}

// Premium notification sound synthesizer using Web Audio API
const playChime = () => {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playBeep = (time: number, freq: number, duration: number) => {
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, time);
      
      gainNode.gain.setValueAtTime(0.12, time);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, time + duration);
      
      osc.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      osc.start(time);
      osc.stop(time + duration);
    };
    
    const now = audioCtx.currentTime;
    playBeep(now, 587.33, 0.25); // D5 chime
    playBeep(now + 0.12, 880, 0.4); // A5 chime
  } catch (err) {
    console.warn("Failed to play chime sound:", err);
  }
};


interface AccessibleWebsite {
  id: string;
  domain: string;
  role?: string | null;
  is_verified?: boolean;
  privacy_policy_url?: string | null;
  privacy_disclaimer_enabled?: boolean | null;
  auto_delete_logs_days?: number | null;
}

interface AccessibleWebsitesResponse {
  error?: string;
  websites?: AccessibleWebsite[] | null;
}

interface DashboardLayoutProps {
  children?: ReactNode;
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hidden?: boolean;
  badge?: number;
}


interface NavGroup {
  title: string;
  items: NavItem[];
}

interface SidebarContentProps {
  isAdminView: boolean;
  adminLinks: NavItem[];
  overviewLink: NavItem;
  clientLinkGroups: NavGroup[];
  sidebarExpanded: boolean;
  setSidebarExpanded: (fn: (prev: boolean) => boolean) => void;
  logoUrl: string | null;
  brandName: string | null;
  hostBrandingChecked: boolean;
  brandingLoaded: boolean;
  setIsOpen: (open: boolean) => void;
  user: any;
  isUserAdmin: boolean;
  websites: AccessibleWebsite[];
  activeWebsiteId: string | null;
  setActiveWebsiteId: (id: string | null) => void;
}

function SidebarContentComponent({
  isAdminView,
  adminLinks,
  overviewLink,
  clientLinkGroups,
  sidebarExpanded,
  setSidebarExpanded,
  logoUrl,
  brandName,
  hostBrandingChecked,
  brandingLoaded,
  setIsOpen,
  user,
  isUserAdmin,
  websites,
  activeWebsiteId,
  setActiveWebsiteId,
}: SidebarContentProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState<'Website' | 'Chatbot' | 'Integration' | 'Team Management' | 'Ecommerce' | 'Billing' | 'Support'>('Chatbot');

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/mini-site') || path.includes('/domains')) {
      setActiveCategory('Website');
    } else if (
      path.includes('/knowledge') || 
      path.includes('/embed') || 
      path.includes('/leads') || 
      path.includes('/chat-logs') || 
      path.includes('/insights') || 
      path.includes('/analytics') ||
      path.includes('/live-inbox') ||
      path.includes('/guide')
    ) {
      setActiveCategory('Chatbot');
    } else if (
      path.includes('/team') || 
      path.includes('/white-label') || 
      path.includes('/clients')
    ) {
      setActiveCategory('Team Management');
    } else if (
      path.includes('/connectors') || 
      path.includes('/api-access')
    ) {
      setActiveCategory('Integration');
    } else if (
      path.includes('/products')
    ) {
      setActiveCategory('Ecommerce');
    } else if (
      path.includes('/plans') || 
      path.includes('/upgrade') || 
      path.includes('/billing')
    ) {
      setActiveCategory('Billing');
    } else if (
      path.includes('/help')
    ) {
      setActiveCategory('Support');
    }
  }, [location.pathname]);

  const categoriesList = [
    { id: 'Website' as const, label: 'Website', icon: Globe, description: 'Mini site & domains' },
    { id: 'Chatbot' as const, label: 'Chatbot AI', icon: MessageSquare, description: 'Knowledge base & widget' },
    { id: 'Ecommerce' as const, label: 'E-commerce', icon: ShoppingBag, description: 'Products & catalog' },
    { id: 'Integration' as const, label: 'Integration', icon: Plug, description: 'WhatsApp & API integrations' },
    { id: 'Team Management' as const, label: 'Team Management', icon: Users, description: 'Manage teams, white-label, & clients' },
    { id: 'Billing' as const, label: 'Billing', icon: CreditCard, description: 'Packages & subscription' },
    { id: 'Support' as const, label: 'Bantuan', icon: HelpCircle, description: 'Panduan & referensi perintah admin' },
  ];

  const handleCategoryChange = (cat: 'Website' | 'Chatbot' | 'Integration' | 'Team Management' | 'Ecommerce' | 'Billing' | 'Support') => {
    setActiveCategory(cat);
    const group = clientLinkGroups.find(g => g.title === cat);
    if (group) {
      const firstVisibleItem = group.items.find(item => !item.hidden);
      if (firstVisibleItem) {
        navigate(firstVisibleItem.to);
        setIsOpen(false);
      }
    }
  };

  const renderLink = (to: string, Icon: React.ComponentType<{ className?: string }>, label: string, badge?: number) => (
    <Link
      key={to}
      to={to}
      onClick={() => setIsOpen(false)}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
        location.pathname === to
          ? 'bg-primary/10 text-foreground font-semibold'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className={cn(
          "ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white",
          label === 'Chat Log' || label === 'Live Inbox' || label === 'Leads'
            ? "bg-purple-600 shadow-[0_0_8px_rgba(147,51,234,0.4)]"
            : "bg-destructive text-destructive-foreground animate-pulse"
        )}>
          {badge}
        </span>
      )}
    </Link>
  );


  return (
    <div className="flex h-full flex-col bg-card">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between gap-2">
          <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setIsOpen(false)}>
            {logoUrl ? (
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                <img src={logoUrl} alt={brandName || 'Logo'} className="h-8 w-8 object-contain" />
              </div>
            ) : (
              <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                <MessageSquare className="h-4 w-4 text-primary-foreground" />
              </div>
            )}
            <span className="font-semibold text-foreground truncate max-w-[150px]">
              {brandName || 'Chatbaze'}
            </span>
          </Link>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
        {isAdminView ? (
          <div className="space-y-1">
            {adminLinks.map(({ to, icon: Icon, label }) => renderLink(to, Icon, label))}
          </div>
        ) : (
          <>
            <div className="space-y-1">
              {renderLink(overviewLink.to, overviewLink.icon, overviewLink.label)}
            </div>

            {/* Global Website Selector Dropdown (Premium Layout) */}
            {activeCategory !== 'Billing' && activeCategory !== 'Website' && activeCategory !== 'Team Management' && websites.length > 0 && (
              <div className="pt-2 border-t border-border/60">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="w-full sm:w-full justify-between gap-2 px-3 py-6 rounded-lg border border-border/60 bg-muted/30 hover:bg-muted/60 text-foreground transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-2.5 text-left min-w-0 flex-1">
                        <div className="bg-primary/10 p-1.5 rounded-md group-hover:scale-105 transition-transform shrink-0">
                          <Globe className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex flex-col min-w-0 overflow-hidden text-left flex-1">
                          <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider leading-none mb-0.5">Situs Web</span>
                          <span className="text-xs font-bold text-foreground truncate">
                            {websites.find(w => w.id === activeWebsiteId)?.domain || 'Pilih Website'}
                          </span>
                        </div>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform group-hover:translate-y-0.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[224px] p-1.5" align="start" sideOffset={4}>
                    <DropdownMenuLabel className="text-[10px] font-bold text-muted-foreground/80 px-2.5 py-1.5 uppercase tracking-wider">
                      Pilih Situs Web
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {websites.map((site) => {
                      const isActive = site.id === activeWebsiteId;
                      return (
                        <DropdownMenuItem
                          key={site.id}
                          onClick={() => {
                            setActiveWebsiteId(site.id);
                          }}
                          className={cn(
                            "flex items-start gap-2.5 p-2 rounded-md cursor-pointer transition-colors mt-1 first:mt-0",
                            isActive 
                              ? "bg-primary/10 text-primary font-medium focus:bg-primary/10 focus:text-primary" 
                              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          )}
                        >
                          <div className={cn(
                            "p-1.5 rounded-md shrink-0",
                            isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                          )}>
                            <Globe className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className={cn("text-xs font-semibold truncate", isActive ? "text-primary" : "text-foreground")}>
                              {site.domain}
                            </span>
                            {site.role && site.role !== 'owner' && (
                              <span className="text-[9px] font-medium text-primary bg-primary/10 px-1 py-0.5 rounded w-max mt-0.5">
                                Tim ({site.role})
                              </span>
                            )}
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <div className="pt-2 border-t border-border/60">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="w-full sm:w-full justify-between gap-2 px-3 py-6 rounded-lg border border-border/60 bg-muted/30 hover:bg-muted/60 text-foreground transition-all duration-200 group"
                  >
                    <div className="flex items-center gap-2.5 text-left">
                      <div className="bg-primary/10 p-1.5 rounded-md group-hover:scale-105 transition-transform shrink-0">
                        <LayoutGrid className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider leading-none mb-0.5">Kategori Fitur</span>
                        <span className="text-xs font-bold text-foreground truncate capitalize">
                          {categoriesList.find(c => c.id === activeCategory)?.label || activeCategory}
                        </span>
                      </div>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform group-hover:translate-y-0.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[224px] p-1.5" align="start" sideOffset={4}>
                  <DropdownMenuLabel className="text-[10px] font-bold text-muted-foreground/80 px-2.5 py-1.5 uppercase tracking-wider">
                    Pilih Kategori Fitur
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {categoriesList.filter(cat => {
                    // For system admin, always show all categories
                    if (isUserAdmin === true) return true;
                    
                    const group = clientLinkGroups.find(g => g.title === cat.id);
                    if (!group) return false;
                    return group.items.some(item => !item.hidden);
                  }).map((cat) => {
                    const Icon = cat.icon;
                    const isActive = activeCategory === cat.id;
                    return (
                      <DropdownMenuItem
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.id)}
                        className={cn(
                          "flex items-start gap-2.5 p-2 rounded-md cursor-pointer transition-colors mt-1 first:mt-0",
                          isActive 
                            ? "bg-primary/10 text-primary font-medium focus:bg-primary/10 focus:text-primary" 
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        )}
                      >
                        <div className={cn(
                          "p-1.5 rounded-md shrink-0",
                          isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                        )}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className={cn("text-xs font-semibold", isActive ? "text-primary" : "text-foreground")}>
                            {cat.label}
                          </span>
                          <span className="text-[10px] text-muted-foreground/80 leading-normal mt-0.5 truncate">
                            {cat.description}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-1 pt-2 border-t border-border/60 animate-in fade-in slide-in-from-top-1 duration-150">
              {clientLinkGroups
                .filter(group => group.title === activeCategory)
                .map(group => {
                  const visibleItems = group.items.filter(item => !item.hidden);
                  if (visibleItems.length === 0) return null;
                  return (
                    <div key={group.title} className="space-y-1">
                      {visibleItems.map(({ to, icon: Icon, label, badge }) => 
                        renderLink(to, Icon, label, badge)
                      )}
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </nav>
    </div>
  );
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, isAdmin, signOut, loading } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [websitesCount, setWebsitesCount] = useState<number | null>(null);
  const [verifiedWebsitesCount, setVerifiedWebsitesCount] = useState<number | null>(null);
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | null>(null);
  const [wizardNextDisabled, setWizardNextDisabled] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [websites, setWebsites] = useState<AccessibleWebsite[]>([]);
  const [activeWebsiteId, setActiveWebsiteId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('global_active_website_id');
    } catch {
      return null;
    }
  });

  const handleWebsiteChange = (id: string | null) => {
    setActiveWebsiteId(id);
    if (id) {
      try {
        localStorage.setItem('global_active_website_id', id);
      } catch { /* ignore */ }
    } else {
      try {
        localStorage.removeItem('global_active_website_id');
      } catch { /* ignore */ }
    }
    window.dispatchEvent(new CustomEvent('website-id-changed', { detail: id }));
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const [userPlanName, setUserPlanName] = useState<string>('Free');
  const [personalPlanName, setPersonalPlanName] = useState<string>('Free');
  const [credits, setCredits] = useState<number>(0);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [isPlanLoading, setIsPlanLoading] = useState(true);

  const fetchPlanAndAccess = useCallback(async (isRefresh = false) => {
    const userId = user?.id;
    if (!userId) {
      setIsPlanLoading(false);
      return;
    }

    let websitesJson: AccessibleWebsitesResponse | null = null;
    try {
      const baseUrl = API_URL.replace(/\/$/, '');
      if (!isRefresh) setIsPlanLoading(true);

      const subTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Subscription Fetch Timeout')), 15000)
      );

      const subQuery = supabase
        .from('user_subscriptions')
        .select('plan_id')
        .eq('user_id', userId)
        .maybeSingle();

      let resolvedPersonalPlanName = 'Free';
      
      // Fetch credits
      try {
        if (userId === '00000000-0000-0000-0000-000000000000' || !userId) {
          setCredits(999999);
        } else {
          const { data: userData } = await supabase
            .from('users')
            .select('credits' as any)
            .eq('id', userId)
            .single();
          if (userData) setCredits((userData as any).credits || 0);
        }
      } catch (e) {
        console.error('[DashboardLayout] Credits error:', e);
      }

      try {
        const { data: sub, error: subError } = (await Promise.race([
          subQuery,
          subTimeout,
        ])) as {
          data: { plan_id?: string | null } | null;
          error: unknown;
        };
        if (subError) console.error('[DashboardLayout] Sub error:', subError);

        if (sub?.plan_id) {
          const { data: plan, error: planError } = await supabase
            .from('plans')
            .select('name')
            .eq('id', sub.plan_id)
            .maybeSingle();

          if (planError) console.error('[DashboardLayout] Plan error:', planError);
          if (plan?.name) resolvedPersonalPlanName = plan.name;
        }
      } catch (subErr) {
        console.warn('[DashboardLayout] Subscription fetch failed or timed out:', subErr);
      }

      const normalizedPersonal =
        resolvedPersonalPlanName.trim().charAt(0).toUpperCase() +
        resolvedPersonalPlanName.trim().slice(1).toLowerCase();

      setPersonalPlanName(normalizedPersonal);

      let normalizedBest = normalizedPersonal;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setUserPlanName(normalizedPersonal);
        setIsTeamMember(false);
        return;
      }

      const flagsRes = await fetch(`${baseUrl}/api/user/best-plan`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'application/json',
        },
      });
      if (flagsRes.status === 401) {
        toast({
          title: 'Sesi berakhir',
          description: 'Sesi login Anda telah kedaluwarsa. Silakan login kembali.',
          variant: 'destructive',
        });
        await signOut();
        return;
      }
      if (flagsRes.ok) {
        try {
          const json = await flagsRes.json() as { planName?: string };
          if (json.planName && json.planName.length > 0) {
            normalizedBest = json.planName.trim().charAt(0).toUpperCase() + 
                            json.planName.trim().slice(1).toLowerCase();
          }
        } catch (e) {
          console.error('Failed to parse best-plan response:', e);
        }
      }

      const res = await fetch(`${baseUrl}/api/websites/accessible`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'application/json',
        },
      });

      const text = await res.text();
      try {
        websitesJson = text ? (JSON.parse(text) as AccessibleWebsitesResponse) : null;
      } catch {
        throw new Error('Respons tidak valid saat memuat daftar situs web');
      }

      if (!res.ok) {
        if (res.status === 401) {
          toast({
            title: 'Sesi berakhir',
            description: 'Sesi login Anda telah kedaluwarsa. Silakan login kembali.',
            variant: 'destructive',
          });
          await signOut();
          return;
        }
        const msg = websitesJson && typeof websitesJson.error === 'string' ? websitesJson.error : text;
        throw new Error(msg || 'Gagal memuat daftar situs web');
      }

      const websitesRaw = websitesJson?.websites;
      const websitesData: AccessibleWebsite[] = Array.isArray(websitesRaw)
        ? websitesRaw.map(w => ({ ...w, id: String(w.id) }))
        : [];
      
      setWebsites(websitesData);
      writeCachedWebsites(userId, websitesData);

      const urlParams = new URLSearchParams(window.location.search);
      const websiteIdFromUrl = urlParams.get('website_id');
      
      let currentId = websiteIdFromUrl || localStorage.getItem('global_active_website_id');
      
      if (websitesData.length > 0) {
        if (!currentId || !websitesData.some(w => String(w.id) === String(currentId))) {
          currentId = websitesData[0].id;
        }
        setActiveWebsiteId(currentId);
        localStorage.setItem('global_active_website_id', currentId);
      } else {
        setActiveWebsiteId(null);
      }

      const isTeam = websitesData.some(w => w.role && w.role !== 'owner');
      setUserPlanName(normalizedBest);
      setIsTeamMember(isTeam);
      writeCachedPlan(userId, {
        userPlanName: normalizedBest,
        personalPlanName: normalizedPersonal,
        isTeamMember: isTeam,
      });

      // Filter owned websites for onboarding check
      const ownedWebsites = websitesData.filter(w => !w.role || w.role === 'owner');
      const vCount = ownedWebsites.filter(w => w.is_verified).length;
      if (vCount === 0) {
        const hasChosen = sessionStorage.getItem(`onboarding_chosen_${user?.id}`);
        if (!hasChosen) setShowOnboardingModal(true);
      }
      setWebsitesCount(ownedWebsites.length);
      setVerifiedWebsitesCount(vCount);

    } catch (err) {
      console.error('CRITICAL: Error in fetchPlanAndAccess:', err);
      setUserPlanName('Free');
      setIsTeamMember(false);
    } finally {
      setIsPlanLoading(false);
    }
  }, [user?.id, toast]);

  useEffect(() => {
    setMounted(true);
  }, []);
  const cachedBranding = readCachedBranding();
  const [brandName, setBrandName] = useState<string | null>(cachedBranding.brandName);
  const [logoUrl, setLogoUrl] = useState<string | null>(cachedBranding.logoUrl);
  const [brandingHost, setBrandingHost] = useState<string | null>(cachedBranding.host);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [hostBrandingChecked, setHostBrandingChecked] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [hostAccessChecked, setHostAccessChecked] = useState(false);
  const [hostAccessError, setHostAccessError] = useState<string | null>(null);
  const [hostAccessLoading, setHostAccessLoading] = useState(false);
  const [liveSessionCount, setLiveSessionCount] = useState(0);
  const [chatLogsUnreadCount, setChatLogsUnreadCount] = useState(0);
  const [leadsUnreadCount, setLeadsUnreadCount] = useState(0);

  const isLeadsFirstLoadRef = useRef(true);
  const isChatLogsFirstLoadRef = useRef(true);
  const isLiveInboxFirstLoadRef = useRef(true);

  useEffect(() => {
    isLeadsFirstLoadRef.current = true;
    isChatLogsFirstLoadRef.current = true;
    isLiveInboxFirstLoadRef.current = true;
  }, [activeWebsiteId]);

  useEffect(() => {
    if (!user?.id || !activeWebsiteId) {
      setChatLogsUnreadCount(0);
      return;
    }
    let isMounted = true;
    const fetchUnreadCount = async () => {
      try {
        // 1. Fetch latest chat logs for this website (limit 200 for performance & fresh relevance)
        const { data: logs, error: logsError } = await (supabase
          .from('chat_logs' as any)
          .select('session_id, created_at, user_message, bot_reply, source, connector_id, is_human_reply' as any) as any)
          .eq('website_id', activeWebsiteId)
          .order('created_at', { ascending: false })
          .limit(200) as any;

        if (logsError || !logs) {
          if (isMounted) setChatLogsUnreadCount(0);
          return;
        }

        // 2. Fetch live sessions status for all sessions in these logs
        const uniqueIds = Array.from(new Set(logs.map((l: any) => l.session_id).filter(Boolean))) as string[];
        if (uniqueIds.length === 0) {
          if (isMounted) setChatLogsUnreadCount(0);
          return;
        }

        const { data: liveSessData, error: liveSessError } = await (supabase
          .from('live_sessions' as any)
          .select('session_id, last_read_at' as any) as any)
          .eq('website_id', activeWebsiteId)
          .in('session_id', uniqueIds) as any;

        const liveSessMap = new Map<string, string | undefined>();
        if (!liveSessError && liveSessData) {
          liveSessData.forEach((ls: any) => {
            liveSessMap.set(ls.session_id, ls.last_read_at);
          });
        }

        // 3. Apply identical comparison logic as ChatLogs.tsx
        const unreadSessions = new Set<string>();
        const sessionFoundReply = new Set<string>();

        for (const log of logs) {
          const sessionId = log.session_id;
          if (sessionFoundReply.has(sessionId)) continue;

          if (log.is_human_reply) {
            // Latest message in this session is an admin reply -> session is read/replied
            sessionFoundReply.add(sessionId);
            continue;
          }

          if (log.user_message) {
            const lastReadAt = liveSessMap.get(sessionId);
            const lastReadStr = lastReadAt && !(lastReadAt.endsWith('Z') || lastReadAt.includes('+')) 
              ? lastReadAt + 'Z' 
              : lastReadAt;
            const lastRead = lastReadStr ? new Date(lastReadStr) : null;
            
            const msgTimeStr = log.created_at.endsWith('Z') || log.created_at.includes('+') ? log.created_at : log.created_at + 'Z';
            const msgTime = new Date(msgTimeStr);

            // Add 3-second buffer to match ChatLogs.tsx
            if (!lastRead || msgTime.getTime() > (lastRead.getTime() + 3000)) {
              unreadSessions.add(sessionId);
            }
            // Once we encounter a user message, we can stop checking older messages of this session
            // because if this one is read, older ones are read too; if this one is unread, we've already marked the session unread.
            sessionFoundReply.add(sessionId);
          }
        }

        if (isMounted) {
          const currentCount = unreadSessions.size;
          setChatLogsUnreadCount(prev => {
            if (!isChatLogsFirstLoadRef.current && currentCount > prev) {
              toast({
                title: "Pesan Baru Masuk! 💬",
                description: "Anda menerima pesan baru dari pengunjung.",
                duration: 5000,
              });
              playChime();
            }
            isChatLogsFirstLoadRef.current = false;
            return currentCount;
          });
        }
      } catch (err) {
        console.error('Error fetching chat logs unread count:', err);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000); // Poll every 10 seconds

    const handleMarkedRead = () => {
      fetchUnreadCount();
    };
    window.addEventListener('chat-read', handleMarkedRead);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener('chat-read', handleMarkedRead);
    };
  }, [user?.id, activeWebsiteId]);

  useEffect(() => {
    if (!user?.id || !activeWebsiteId) {
      setLeadsUnreadCount(0);
      return;
    }
    let isMounted = true;
    const fetchLeadsUnreadCount = async () => {
      try {
        let lastViewed = localStorage.getItem('last_viewed_leads_at_' + activeWebsiteId);
        if (!lastViewed) {
          lastViewed = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
          localStorage.setItem('last_viewed_leads_at_' + activeWebsiteId, lastViewed);
        }

        const { count, error } = await supabase
          .from('leads' as any)
          .select('id' as any, { count: 'exact', head: true } as any)
          .eq('website_id', activeWebsiteId)
          .gt('created_at', lastViewed) as any;

        if (error) {
          console.error('Error fetching leads unread count:', error);
          return;
        }

        if (isMounted) {
          const currentCount = count || 0;
          setLeadsUnreadCount(prev => {
            if (!isLeadsFirstLoadRef.current && currentCount > prev) {
              toast({
                title: "Lead Baru Didapatkan! 🎉",
                description: "Seseorang baru saja mengisi formulir kontak.",
                duration: 5000,
              });
              playChime();
            }
            isLeadsFirstLoadRef.current = false;
            return currentCount;
          });
        }
      } catch (err) {
        console.error('Error in fetchLeadsUnreadCount:', err);
      }
    };

    fetchLeadsUnreadCount();
    const interval = setInterval(fetchLeadsUnreadCount, 10000); // Poll every 10 seconds

    const handleLeadsRead = () => {
      if (isMounted) {
        setLeadsUnreadCount(0);
      }
    };
    window.addEventListener('leads-read', handleLeadsRead);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener('leads-read', handleLeadsRead);
    };
  }, [user?.id, activeWebsiteId]);

  // Re-fetch websites when triggered by child components (like Domains.tsx)
  useEffect(() => {
    const handleUpdate = () => {
      setRefreshTrigger(prev => prev + 1);
    };
    window.addEventListener('websites-update', handleUpdate);
    return () => window.removeEventListener('websites-update', handleUpdate);
  }, []);


  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Poll live session count every 15 seconds (for badge on sidebar)
  useEffect(() => {
    if (!user?.id) return;
    let isMounted = true;
    const fetchCount = async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        if (!sess?.session?.access_token || !isMounted) return;
        const res = await fetch(`${API_URL}/api/handoff/active${activeWebsiteId ? `?website_id=${activeWebsiteId}` : ''}`, {
          headers: { Authorization: `Bearer ${sess.session.access_token}` },
        });
        if (res.status === 401 && isMounted) {
          toast({
            title: 'Sesi berakhir',
            description: 'Sesi login Anda telah kedaluwarsa. Silakan login kembali.',
            variant: 'destructive',
          });
          await signOut();
          return;
        }
        if (res.ok && isMounted) {
          const data = await res.json() as { sessions?: any[] };
          const currentCount = data.sessions?.filter(s => s.is_read === false).length || 0;
          setLiveSessionCount(prev => {
            if (!isLiveInboxFirstLoadRef.current && currentCount > prev) {
              toast({
                title: "Handoff Baru! 🎧",
                description: "Seorang pengunjung meminta bantuan agen manusia.",
                duration: 5000,
              });
              playChime();
            }
            isLiveInboxFirstLoadRef.current = false;
            return currentCount;
          });
        }
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [user?.id, activeWebsiteId]);


  useEffect(() => {
    if (!mounted || !user?.id || typeof window === 'undefined') {
      return;
    }
    try {
      const storedTheme = window.localStorage.getItem(`dashboard_theme_${user.id}`);
      if ((storedTheme === 'light' || storedTheme === 'dark') && storedTheme !== theme) {
        setTheme(storedTheme);
      }
    } catch (err) {
      console.error('Failed to load dashboard theme', err);
    }
  }, [mounted, user?.id, setTheme, theme]);

  useEffect(() => {
    let mounted = true;
    const userId = user?.id;
    if (!userId) {
      if (mounted) setIsPlanLoading(false);
      return () => {
        mounted = false;
      };
    }

    // Load cached websites immediately so they render without any layout shift
    const cachedWebsites = readCachedWebsites(userId);
    if (cachedWebsites.length > 0 && mounted) {
      setWebsites(cachedWebsites.map(w => ({ ...w, id: String(w.id) })));
    }

    const existingPlan = readCachedPlan(userId);

    if (existingPlan) {
      if (mounted) {
        setUserPlanName(existingPlan.userPlanName);
        setPersonalPlanName(existingPlan.personalPlanName);
        setIsTeamMember(existingPlan.isTeamMember);
        setIsPlanLoading(false);
      }
    }

    fetchPlanAndAccess();
    return () => {
      mounted = false;
    };
  }, [user?.id, refreshTrigger, fetchPlanAndAccess]);

  useEffect(() => {
    let aborted = false;

    const checkHostAccess = async () => {
      if (!user?.id) {
        if (!aborted) {
          setHostAccessChecked(false);
          setHostAccessError(null);
          setHostAccessLoading(false);
        }
        return;
      }
      if (typeof window === 'undefined') {
        if (!aborted) {
          setHostAccessChecked(true);
          setHostAccessError(null);
          setHostAccessLoading(false);
        }
        return;
      }
      try {
        if (!aborted) {
          setHostAccessLoading(true);
        }
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          if (!aborted) {
            setHostAccessChecked(true);
            setHostAccessError(null);
          }
          return;
        }
        const host = window.location.host;
        const normalizedApiUrl = normalizeApiUrl(API_URL);
        const res = await fetch(`${normalizedApiUrl}/api/agency/host-access?host=${encodeURIComponent(host)}`, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            Accept: 'application/json',
          },
        });
        const text = await res.text();
        let json: { allowed?: unknown; error?: unknown } | null = null;
        try {
          json = text ? (JSON.parse(text) as { allowed?: unknown; error?: unknown }) : null;
        } catch (err) {
          console.error('[DashboardLayout] Failed to parse host access response', err);
        }
        if (aborted) {
          return;
        }
        if (!res.ok) {
          const msg =
            json && typeof json.error === 'string'
              ? json.error
              : text || 'Akses ditolak untuk domain dashboard ini';
          if (res.status === 403) {
            setHostAccessError(msg);
          } else {
            setHostAccessError(null);
          }
          setHostAccessChecked(true);
          return;
        }
        const allowed =
          json && typeof json.allowed === 'boolean' ? json.allowed : true;
        if (!allowed) {
          const msg =
            json && typeof json.error === 'string'
              ? json.error
              : 'Akses ditolak untuk domain dashboard ini';
          setHostAccessError(msg);
        } else {
          setHostAccessError(null);
        }
        setHostAccessChecked(true);
      } catch (err) {
        console.error('[DashboardLayout] Host access check failed', err);
        if (!aborted) {
          setHostAccessChecked(true);
        }
      } finally {
        if (!aborted) {
          setHostAccessLoading(false);
        }
      }
    };

    checkHostAccess();
    return () => {
      aborted = true;
    };
  }, [user?.id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wizardParam = params.get('wizard');
    if (wizardParam === 'step1') setWizardStep(1);
    else if (wizardParam === 'step2') setWizardStep(2);
    else if (wizardParam === 'step3') setWizardStep(3);
    else setWizardStep(null);
  }, [location.search]);

  useEffect(() => {
    const totalUnread = chatLogsUnreadCount + leadsUnreadCount;
    const prefix = totalUnread > 0 ? `(${totalUnread}) ` : '';
    if (!brandName) {
      document.title = `${prefix}Chatbaze Dashboard`;
    } else {
      document.title = `${prefix}${brandName} Dashboard`;
    }
  }, [brandName, chatLogsUnreadCount, leadsUnreadCount]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const path = location.pathname;
    if (path.startsWith('/dashboard') || path.startsWith('/admin')) {
      try {
        window.localStorage.setItem('last_dashboard_path', path);
      } catch {
        void 0;
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const currentHost = window.location.host.toLowerCase();
    if (brandingHost && brandingHost === currentHost) {
      setHostBrandingChecked(true);
      return;
    }
    let aborted = false;
    const run = async () => {
      try {
        const baseUrl = API_URL.replace(/\/$/, '');
        const res = await fetch(
          `${baseUrl}/api/agency/branding`
        );
        if (aborted) return;
        if (!res.ok) {
          setHostBrandingChecked(true);
          return;
        }
        const data = await res.json();
        if (aborted) return;
        const newBrandName = (data && data.brand_name) || null;
        const newLogoUrl = (data && data.logo_url) || null;
        setBrandName(newBrandName);
        setLogoUrl(newLogoUrl);
        setBrandingHost(currentHost);
        window.localStorage.setItem(
          'agency_branding',
          JSON.stringify({
            brand_name: newBrandName,
            logo_url: newLogoUrl,
            host: currentHost,
          })
        );
        setHostBrandingChecked(true);
      } catch (e) {
        if (!aborted) setHostBrandingChecked(true);
      }
    };
    run();
    return () => {
      aborted = true;
    };
  }, [brandingHost]);

  useEffect(() => {
    let mounted = true;
    const fetchBranding = async () => {
      if (!user?.id) {
        if (mounted) {
          setBrandingLoaded(false);
        }
        return;
      }
      // Tunggu sampai pengecekan plan selesai agar tidak ada flicker 'Chatbaze'
      // saat transisi dari default 'Free' ke 'Agency'
      if (isPlanLoading) return;

      if (userPlanName !== 'Agency') {
        if (mounted) {
          setBrandingLoaded(true);
        }
        return;
      }
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          if (mounted) setBrandingLoaded(true);
          return;
        }
        const res = await fetch(`${API_URL}/api/agency/settings`, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        if (!mounted) return;
        if (!res.ok) {
          setBrandingLoaded(true);
          return;
        }
        const data = await res.json();
        const newBrandName = (data && data.brand_name) || null;
        const newLogoUrl = (data && data.logo_url) || null;
        setBrandName(newBrandName);
        setLogoUrl(newLogoUrl);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(
            'agency_branding',
            JSON.stringify({ brand_name: newBrandName, logo_url: newLogoUrl })
          );
        }
        setBrandingLoaded(true);
      } catch (e) {
        if (mounted) {
          setBrandingLoaded(true);
        }
      }
    };
    fetchBranding();
    return () => {
      mounted = false;
    };
  }, [user?.id, userPlanName, isPlanLoading]);

  const isAgency = userPlanName.toLowerCase().includes('agency') || personalPlanName.toLowerCase().includes('agency');
  const isGrowth = userPlanName.toLowerCase().includes('growth') || personalPlanName.toLowerCase().includes('growth');
  const isUserAdmin = isSystemAdmin(user, isAdmin);
  const overviewLink = { to: '/dashboard', icon: BarChart3, label: 'Dashboard' };

  const clientLinkGroups: NavGroup[] = [
    {
      title: 'Website',
      items: [
        { to: '/dashboard/domains', icon: Globe, label: 'Domain' },
        { to: '/dashboard/mini-site', icon: LayoutTemplate, label: 'Mini Site' },
      ],
    },
    {
      title: 'Chatbot',
      items: [
        { to: '/dashboard/knowledge', icon: FileText, label: 'Knowledge' },
        { to: '/dashboard/embed', icon: Code, label: 'Embed Script' },
        { 
          to: '/dashboard/leads', 
          icon: Users, 
          label: 'Leads',
          badge: leadsUnreadCount > 0 ? leadsUnreadCount : undefined,
        },
        { 
          to: '/dashboard/chat-logs', 
          icon: MessageSquare, 
          label: 'Chat Log',
          badge: chatLogsUnreadCount > 0 ? chatLogsUnreadCount : undefined,
        },
        { to: '/dashboard/insights', icon: Brain, label: 'Insights' },
        {
          to: '/dashboard/live-inbox',
          icon: Headphones,
          label: 'Live Inbox',
          badge: liveSessionCount > 0 ? liveSessionCount : undefined,
          hidden: !(isGrowth || isAgency || isUserAdmin),
        },
        { to: '/dashboard/analytics', icon: TrendingUp, label: 'Analytics' },
      ],
    },
    {
      title: 'Ecommerce',
      items: [
        { to: '/dashboard/products', icon: ShoppingBag, label: 'Products' },
      ],
    },
    {
      title: 'Integration',
      items: [
        { 
          to: '/dashboard/connectors', 
          icon: Database, 
          label: 'Data Connectors',
          hidden: personalPlanName.toLowerCase().includes('free') && isUserAdmin !== true
        },
        { to: '/dashboard/api-access', icon: Code, label: 'API Access' },
      ],
    },
    {
      title: 'Team Management',
      items: [
        {
          to: '/dashboard/team',
          icon: UserCircle,
          label: 'Team',
          hidden: !(isAgency || isGrowth || isTeamMember || isUserAdmin),
        },
        {
          to: '/dashboard/white-label',
          icon: Settings,
          label: 'White Label',
          hidden: !(isAgency || isGrowth || isUserAdmin),
        },
        {
          to: '/dashboard/clients',
          icon: Users,
          label: 'Client',
          hidden: !(isAgency || isUserAdmin),
        },
      ],
    },
    {
      title: 'Billing',
      items: [
        { to: '/dashboard/plans', icon: Zap, label: 'Packages' },
        { to: '/dashboard/upgrade', icon: Rocket, label: 'Upgrade' },
        { to: '/dashboard/billing', icon: CreditCard, label: 'Billing' },
      ],
    },
    {
      title: 'Support',
      items: [
        { to: '/dashboard/help', icon: HelpCircle, label: 'Bantuan Admin' },
      ],
    },
  ];

  // Debug log for links
  useEffect(() => {
    if (!isPlanLoading && user?.email) {
      console.log('--- DASHBOARD DEBUG START ---');
      console.log('User Email:', user.email);
      console.log('Is User Admin:', isUserAdmin);
      console.log('User Plan Name:', userPlanName);
      console.log('Personal Plan Name:', personalPlanName);
      console.log('Is Agency:', isAgency);
      console.log('Is Growth:', isGrowth);
      console.log('Is Team Member:', isTeamMember);
      
      const teamMgmtGroup = clientLinkGroups.find(g => g.title === 'Team Management');
      if (teamMgmtGroup) {
        console.log('Team Management Items Visibility:');
        teamMgmtGroup.items.forEach(item => {
          console.log(`- ${item.label}: hidden = ${item.hidden}`);
        });
      }
      console.log('--- DASHBOARD DEBUG END ---');
    }
  }, [userPlanName, personalPlanName, isAgency, isGrowth, isUserAdmin, isTeamMember, isPlanLoading, user, clientLinkGroups]);

  // Only block render before we know whether user is logged in.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const adminLinks = [
    { to: '/admin', icon: BarChart3, label: 'Dashboard' },
    { to: '/admin/users', icon: Users, label: 'Pengguna' },
    { to: '/admin/plans', icon: CreditCard, label: 'Packages' },
    { to: '/admin/invoices', icon: CreditCard, label: 'Invoice' },
    { to: '/admin/vouchers', icon: Ticket, label: 'Voucher' },
    { to: '/admin/email', icon: Mail, label: 'Email' },
    { to: '/admin/settings', icon: Settings, label: 'Pengaturan' },
    { to: '/admin/monitoring', icon: Activity, label: 'Monitoring' },
  ];

  const isOnAdminRoute = location.pathname.startsWith('/admin');

  const getPlanDotColor = (planName?: string) => {
    const name = planName?.toLowerCase() || '';
    if (name.includes('growth')) return 'bg-emerald-500';
    if (name.includes('agency')) return 'bg-rose-500';
    if (name.includes('pro')) return 'bg-indigo-500';
    if (name.includes('business')) return 'bg-violet-500';
    if (name.includes('enterprise')) return 'bg-amber-500';
    if (name.includes('free')) return 'bg-secondary';
    return 'bg-secondary';
  };

  const handleSignOut = async () => {
    try {
      // Clear specific dashboard caches to prevent stale data or redirection loops
      window.localStorage.removeItem('last_dashboard_path');
      window.localStorage.removeItem('dashboard_plan_cache');
      
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during sign out:', error);
      // Fallback navigate
      navigate('/login', { replace: true });
    }
  };

  const handleToggleTheme = () => {
    // Use resolvedTheme to correctly determine current dark/light status even when "system"
    const nextTheme = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    if (typeof window !== 'undefined' && user?.id) {
      try {
        window.localStorage.setItem(`dashboard_theme_${user.id}`, nextTheme);
      } catch (err) {
        console.error('Failed to save dashboard theme', err);
      }
    }
  };

  const appMeta = user?.app_metadata as
    | {
        provider?: string;
        providers?: string[];
      }
    | undefined;

  const isGoogleUser =
    appMeta?.provider === 'google' ||
    (Array.isArray(appMeta?.providers) && appMeta.providers.includes('google'));

  const userMeta = user?.user_metadata as
    | {
        full_name?: string;
        name?: string;
        avatar_url?: string;
        picture?: string;
      }
    | undefined;

  const googleFullName = userMeta?.full_name || userMeta?.name || undefined;

  const avatarUrl =
    isGoogleUser &&
    (typeof userMeta?.avatar_url === 'string'
      ? userMeta.avatar_url
      : typeof userMeta?.picture === 'string'
      ? userMeta.picture
      : null);

  const displayName =
    (isGoogleUser && googleFullName) ||
    (userMeta?.full_name as string) ||
    (user?.email ? user.email.split('@')[0] : 'Account');

  const initials = displayName
    .split(' ')
    .map(part => part[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const isDarkMode = resolvedTheme === 'dark';

  const isAdminView = isUserAdmin && isOnAdminRoute;
  const sidebarProps: SidebarContentProps = {
    isAdminView,
    adminLinks,
    overviewLink,
    clientLinkGroups,
    sidebarExpanded,
    setSidebarExpanded,
    logoUrl,
    brandName,
    hostBrandingChecked,
    brandingLoaded,
    setIsOpen,
    user,
    isUserAdmin,
    websites,
    activeWebsiteId,
    setActiveWebsiteId: handleWebsiteChange,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen flex bg-background", !user && "justify-center")}>
      {/* Desktop Sidebar */}
      {user && (
        <aside className={cn(
          "hidden md:flex w-64 flex-col border-r border-border bg-card fixed inset-y-0 z-50 transition-opacity",
          wizardStep && "opacity-50 pointer-events-none"
        )}>
          <SidebarContentComponent {...sidebarProps} />
          {wizardStep && (
            <div className="absolute inset-0 bg-background/20 backdrop-blur-[1px] flex items-center justify-center p-4 text-center">
              <p className="text-[10px] font-medium text-muted-foreground bg-background/80 px-2 py-1 rounded border border-border shadow-sm">
                Menu dikunci selama Wizard
              </p>
            </div>
          )}
        </aside>
      )}

      {/* Mobile Sidebar (Sheet) */}
      {user && (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden fixed top-4 left-4 z-40 bg-background/80 backdrop-blur-sm border border-border shadow-sm">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64 [&>button]:hidden">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <SidebarContentComponent {...sidebarProps} />
          </SheetContent>
        </Sheet>
      )}

      {/* Main content */}
      <main className={cn("flex-1 transition-all duration-300 ease-in-out", user ? "md:pl-64" : "max-w-5xl")}>
        {user && (
          <div className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur-sm">
            <div className="w-full lg:max-w-[1304px] mx-auto h-16 px-4 md:px-8 flex items-center justify-end gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center gap-3 rounded-md px-3 py-1.5 text-sm text-foreground bg-transparent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                    <div className="flex items-center gap-2">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={displayName}
                          className="h-8 w-8 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                          {initials}
                        </div>
                      )}
                      <div className="hidden sm:flex flex-col items-start">
                        <span className="text-sm font-medium leading-tight capitalize">{displayName}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[80px] sm:max-w-[180px]">
                          {user?.email}
                        </span>
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>
                    <div className="flex items-center gap-3">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={displayName}
                          className="h-9 w-9 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                          {initials}
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm font-medium leading-tight">{displayName}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                          {user?.email}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/profile" className="cursor-pointer">
                      <UserCircle className="mr-2 h-4 w-4" />
                      <span>Profile Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  {isUserAdmin && (
                    <DropdownMenuItem
                      onClick={() => {
                        navigate(isOnAdminRoute ? '/dashboard' : '/admin');
                        setIsOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      {isOnAdminRoute ? (
                        <>
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          <span>Client Dashboard</span>
                        </>
                      ) : (
                        <>
                          <Shield className="mr-2 h-4 w-4" />
                          <span>Admin Dashboard</span>
                        </>
                      )}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleToggleTheme}
                    className="cursor-pointer"
                  >
                    {isDarkMode ? (
                      <Sun className="mr-2 h-4 w-4" />
                    ) : (
                      <Moon className="mr-2 h-4 w-4" />
                    )}
                    <span>
                      Mode: {isDarkMode ? 'Terang' : 'Gelap'}
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        {wizardStep && (
          <div className="sticky top-[64px] z-40 w-full overflow-hidden">
            <WizardBanner 
              step={wizardStep} 
              isNextDisabled={wizardNextDisabled}
              nextUrl={
                wizardStep === 1 ? "/dashboard/knowledge?wizard=step2" :
                wizardStep === 2 ? "/dashboard/embed?wizard=step3" :
                "/dashboard"
              }
            />
          </div>
        )}

        <div className={cn(
          "w-full mx-auto", 
          user ? "lg:max-w-[1304px] px-3 py-3 sm:px-4 sm:py-4 md:p-8 min-h-screen overflow-x-hidden" : "p-4 md:p-8 pt-12"
        )}>
          {/* Show access error inline without unmounting sidebar */}
          {hostAccessError ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4 px-4">
              <h1 className="text-2xl font-bold tracking-tight">Access Restricted</h1>
              <p className="text-muted-foreground text-sm">{hostAccessError}</p>
              <p className="text-muted-foreground text-xs">
                This dashboard domain belongs to another agency. You cannot access this dashboard with your current account.
              </p>
              <Button
                variant="outline"
                onClick={async () => {
                  await signOut();
                  window.location.replace('/login');
                }}
              >
                Log out
              </Button>
            </div>
          ) : user && hostAccessLoading && !hostAccessChecked ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {(verifiedWebsitesCount === 0) && !wizardStep && !showOnboardingModal && user?.id && (
                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 duration-500">
                  <div className="flex items-center gap-3 text-left">
                    <div className="bg-emerald-100 p-2 rounded-lg shrink-0">
                      <Zap className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-emerald-900">Belum memiliki website?</h3>
                      <p className="text-xs text-emerald-700">Mulai alur setup terbimbing untuk membantu Anda memasang chatbot dalam 3 langkah mudah.</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => navigate('/dashboard/domains?wizard=step1')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                  >
                    Mulai Setup Wizard
                  </Button>
                </div>
              )}
              <Outlet context={{ 
                activeWebsiteId, 
                setActiveWebsiteId: handleWebsiteChange, 
                websites, 
                setWizardNextDisabled,
                refreshWebsites: () => fetchPlanAndAccess(true)
              }} />
              {children}
            </>
          )}
        </div>
      </main>

      <OnboardingChoiceModal 
        open={showOnboardingModal} 
        onChoice={(choice) => {
          setShowOnboardingModal(false);
          if (user?.id) {
            sessionStorage.setItem(`onboarding_chosen_${user.id}`, 'true');
          }
          if (choice === 'wizard') {
            navigate('/dashboard/domains?wizard=step1');
          }
        }} 
      />
    </div>
  );
}
 
